import { hash } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import { coreActions } from '../core-actions';

import * as actions from './categoriesActions';
import * as categoriesTransformers from './categoriesTransformers';
import * as categoriesTypes from './categoriesTypes';
import type { CategoryProps } from './categoriesTypes';

export const QcsSyncCategoriesStoreFactory = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<CategoryProps>();

const reducer = handleActions<
  resourceStoreTypes.QcsSyncResourceStore<CategoryProps>,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.createCategory>
  | ReturnType<typeof actions.createCategorySuccess>
  | ReturnType<typeof actions.updateCategory>
  | ReturnType<typeof actions.updateCategorySuccess>
  | ReturnType<typeof actions.deleteCategory>
  | ReturnType<typeof actions.deleteCategorySuccess>
  | ReturnType<typeof actions.batchCategories>
  | ReturnType<typeof actions.batchCategoriesSuccess>
  | ReturnType<typeof actions.getCategories>
  | ReturnType<typeof actions.getCategoriesSuccess>
  | ReturnType<typeof actions.getCategoriesFailure>
  | ReturnType<typeof actions.deleteAndReassignCategory>
  | ReturnType<typeof actions.deleteAndReassignCategorySuccess>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.merge({
            name: hash(resource.name).toString(),
          }),
        ),
      ),

    [actions.createCategory.type]: (state, { payload: category }) => resourceUpserters.upsertResource(state, category),
    [actions.createCategorySuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteCategory),

    [actions.updateCategory.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.updateCategory),
    [actions.updateCategorySuccess.type]: (state, { payload: category, asyncDispatch }) => {
      asyncDispatch(actions.getCategories());
      return resourceUpserters.upsertResource(state, category);
    },

    [actions.deleteAndReassignCategory.type]: (state, action) =>
      resourceUpserters.upsertResource(state, categoriesTypes.mkCategory({ id: action.payload.id, isDeleted: true })),
    [actions.deleteAndReassignCategorySuccess.type]: (state, action) =>
      resourceUpserters.upsertResource(
        state,
        categoriesTransformers.transformServerResourceToClientResource(action.payload.data?.resource),
      ),

    [actions.deleteCategory.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createCategory),
    [actions.deleteCategorySuccess.type]: (state, { payload: category }) =>
      resourceUpserters.upsertResource(state, category),

    [actions.batchCategories.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchCategories, false),
    [actions.batchCategoriesSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchCategories, true),

    [combineActions(
      actions.createCategoryFailure,
      actions.updateCategoryFailure,
      actions.batchCategoriesFailure,
      actions.deleteCategoryFailure,
      actions.deleteAndReassignCategoryFailure,
    ).toString()]: (state: any, { payload: error, asyncDispatch }: any) => {
      // TODO: remove async dispatch and instead create a saga that listens for the failure actions and dispatches get categories
      asyncDispatch(actions.getCategories());
      return resourceUpserters.resetResources(state, error);
    },

    [actions.getCategories.type]: (state) => state.set('isLoading', true),
    [actions.getCategoriesSuccess.type]: (state, { payload: data }) => resourceUpserters.upsertResources(state, data),
    [actions.getCategoriesFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),
  },
  QcsSyncCategoriesStoreFactory({ resourceName: 'CATEGORIES' }),
);

export default reducer;
